"use client";

/**
 * use-unified-genres.ts — ONE genre source of truth for every input on BB.
 *
 * The codebase grew three parallel genre stores over time:
 *
 *   1. src/lib/genres.ts:GENRE_LABELS — static seed list
 *   2. services/app-config.ts:getGenreConfig — curated `genres/{id}` Firestore
 *      collection. Admin edits this in Super Admin → Config Lists.
 *   3. lib/genres.ts:fetchAllGenresFromDB — scans artists + releases + works
 *      and merges every unique string found. Used by MultiGenreInput.
 *
 * That gave us different autocomplete suggestions on different pages and
 * meant a typo on one upload polluted the suggestion list everywhere.
 *
 * The unified hook returns a sorted, deduped list seeded from #1, layered
 * with #2 (Firestore canonical), and writes any new entry the user types
 * back into #2 so the next opener of any form sees it. #3 is no longer
 * called from any input — it remains only as a one-shot import / migration
 * helper if we ever want to back-fill from existing content.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { GENRE_LABELS, normalizeGenre } from "@/lib/genres";

const COL = "genres";

/** Module-level cache so multiple inputs on the same page share one read. */
let _cache: Set<string> | null = null;
let _cacheTime = 0;
const TTL_MS = 60_000;
const _subscribers = new Set<(snapshot: string[]) => void>();

async function loadFromDB(): Promise<Set<string>> {
  const next = new Set<string>(GENRE_LABELS);
  try {
    const snap = await getDocs(collection(db, COL));
    snap.docs.forEach(d => {
      const v = d.data() as { label?: string };
      const label = (v.label || d.id || "").trim();
      if (label) next.add(label);
    });
  } catch (err) {
    console.warn("[useUnifiedGenres] Firestore read failed; seed only.", err);
  }
  return next;
}

async function getCache(): Promise<Set<string>> {
  const now = Date.now();
  if (_cache && now - _cacheTime < TTL_MS) return _cache;
  _cache = await loadFromDB();
  _cacheTime = now;
  return _cache;
}

function notify() {
  if (!_cache) return;
  const arr = Array.from(_cache).sort();
  _subscribers.forEach(fn => fn(arr));
}

/**
 * Write a genre back to the canonical `genres/{slug}` collection. Idempotent —
 * a doc that exists is left alone. Called automatically by MultiGenreInput
 * when the user types a value that isn't already in the list.
 *
 * `userSubmitted: true` is stamped so Super Admin → Config Lists can review
 * incoming entries and merge / normalise them. Includes timestamps for
 * audit. Skips writes for entries that match the static seed list — those
 * are the canonical baseline.
 */
export async function addCanonicalGenre(label: string): Promise<void> {
  const clean = normalizeGenre(label).trim();
  if (!clean) return;
  // Cheap dedupe — refresh cache first so we don't write something the
  // server already has.
  const cache = await getCache();
  if (cache.has(clean)) return;
  // Don't write the static-seed labels — they're not real Firestore docs.
  if ((GENRE_LABELS as string[]).some(l => l.toLowerCase() === clean.toLowerCase())) {
    cache.add(clean);
    notify();
    return;
  }
  const id = clean.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "genre";
  try {
    await setDoc(doc(db, COL, id), {
      label: clean,
      userSubmitted: true,
      createdAt: serverTimestamp(),
    }, { merge: true });
    cache.add(clean);
    notify();
  } catch (err) {
    console.warn("[useUnifiedGenres] write failed", err);
  }
}

/**
 * React hook — returns `{ items, loading, add }` where `items` is the full
 * sorted list and `add(label)` persists a new genre to Firestore + the
 * shared cache. Components subscribe so a write in one input updates the
 * autocomplete in every other open input on the page.
 */
export function useUnifiedGenres(): {
  items: string[];
  loading: boolean;
  add: (label: string) => Promise<void>;
} {
  const [items, setItems] = useState<string[]>(() => Array.from(new Set<string>(GENRE_LABELS)).sort());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getCache().then(set => {
      if (cancelled) return;
      setItems(Array.from(set).sort());
      setLoading(false);
    });
    const subscriber = (next: string[]) => setItems(next);
    _subscribers.add(subscriber);
    return () => {
      cancelled = true;
      _subscribers.delete(subscriber);
    };
  }, []);

  const add = useCallback(async (label: string) => {
    await addCanonicalGenre(label);
  }, []);

  return useMemo(() => ({ items, loading, add }), [items, loading, add]);
}
